import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useI18n } from "../../contexts/I18nContext";
import LanguageToggle from "./LanguageToggle";
import NotificationsBell from "./NotificationsBell";
import MobileBottomNav from "./MobileBottomNav";
import Avatar from "./Avatar";
import {
  LayoutDashboard, Users, Calendar, MessageSquare, LogOut, Menu, X,
  Trophy, Search, Sparkles, Settings, ChevronLeft, ChevronRight, CreditCard, Map as MapIcon, Shield, Heart,
  Inbox, DollarSign,
  Contact, Layers, BadgeDollarSign,
} from "lucide-react";
import Logo from "./Logo";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    typeof window !== "undefined" && localStorage.getItem("sidebar-collapsed") === "1"
  );

  const toggleCollapsed = () => {
    const v = !collapsed;
    setCollapsed(v);
    localStorage.setItem("sidebar-collapsed", v ? "1" : "0");
  };

  // Role-based navigation — Iter 29 enforces strict separation:
  //   PARENT  → My Children · Calendar · Sessions · Chat · Favorites · Discover · Map
  //   COACH   → Players · Calendar · Reservations · Earnings · Chat
  //   ACADEMY → Players · Coaches · Calendar · Applications · Chat
  //   PLAYER  → Discover · Map · Sessions · Chat
  const role = user?.role;
  const dashboardItem = { to: "/", label: t("dashboard"), icon: LayoutDashboard, end: true };

  let roleItems = [];
  if (role === "parent") {
    // Parent is a follower/supporter account — minimal: stays focused on
    // tracking sessions + chat + discovery. No "player management" feeling.
    roleItems = [
      { to: "/calendar", label: t("calendar"), icon: Calendar },
      { to: "/bookings", label: lang === "bg" ? "Сесии" : "Sessions", icon: CreditCard },
      { to: "/chat", label: t("chat"), icon: MessageSquare },
      { to: "/favorites", label: lang === "bg" ? "Любими" : "Favorites", icon: Heart },
      { to: "/find-coaches", label: lang === "bg" ? "Открий" : "Discover", icon: Search },
      { to: "/coaches-map", label: t("mapView"), icon: MapIcon },
    ];
  } else if (role === "coach") {
    roleItems = [
      { to: "/children", label: lang === "bg" ? "Играчи" : "Players", icon: Users },
      { to: "/calendar", label: t("calendar"), icon: Calendar },
      { to: "/bookings", label: lang === "bg" ? "Резервации" : "Reservations", icon: CreditCard },
      { to: "/earnings", label: lang === "bg" ? "Приходи" : "Earnings", icon: DollarSign },
      { to: "/customers", label: lang === "bg" ? "Клиенти" : "Customers", icon: Contact },
      { to: "/chat", label: t("chat"), icon: MessageSquare },
    ];
  } else if (role === "academy") {
    roleItems = [
      { to: "/children", label: lang === "bg" ? "Играчи" : "Players", icon: Users },
      { to: "/academy/coaches", label: lang === "bg" ? "Треньори" : "Coaches", icon: Trophy },
      { to: "/calendar", label: t("calendar"), icon: Calendar },
      { to: "/academy/programs", label: lang === "bg" ? "Програми" : "Programs", icon: Layers },
      { to: "/bookings", label: lang === "bg" ? "Резервации" : "Bookings", icon: CreditCard },
      { to: "/customers", label: lang === "bg" ? "Клиенти" : "Customers", icon: Contact },
      { to: "/earnings", label: lang === "bg" ? "Приходи" : "Revenue", icon: DollarSign },
      { to: "/academy/subscription", label: lang === "bg" ? "Абонамент" : "Plan", icon: BadgeDollarSign },
      { to: "/academy/applications", label: lang === "bg" ? "Заявки" : "Applications", icon: Inbox },
      { to: "/chat", label: t("chat"), icon: MessageSquare },
    ];
  } else if (role === "player") {
    roleItems = [
      { to: "/find-coaches", label: lang === "bg" ? "Открий" : "Discover", icon: Search },
      { to: "/coaches-map", label: t("mapView"), icon: MapIcon },
      { to: "/bookings", label: lang === "bg" ? "Сесии" : "Sessions", icon: CreditCard },
      { to: "/chat", label: t("chat"), icon: MessageSquare },
    ];
  }

  const items = [
    dashboardItem,
    ...roleItems,
    { to: "/settings", label: lang === "bg" ? "Настройки" : "Settings", icon: Settings },
    ...(user?.is_admin ? [{ to: "/admin", label: lang === "bg" ? "Админ" : "Admin", icon: Shield }] : []),
  ];

  const handleLogout = async () => { await logout(); nav("/login"); };

  const widthClass = collapsed ? "md:w-[72px]" : "md:w-64";

  return (
    <div className="min-h-screen flex flex-col md:flex-row text-white">
      {/* Sidebar */}
      <aside
        className={`fixed md:sticky md:top-0 inset-y-0 left-0 z-40 w-64 ${widthClass} glass-strong border-r border-white/10 transform transition-all duration-300 ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 md:h-screen flex flex-col`}
        data-testid="sidebar"
      >
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
            {collapsed ? <Logo size={28} variant="icon" /> : <Logo size={26} wordmark />}
          </Link>
          <button className="md:hidden text-white/60" onClick={() => setOpen(false)} data-testid="close-sidebar">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!collapsed && (
          <div className="p-4 border-b border-white/10 flex items-center gap-3">
            <Avatar name={user?.name} src={user?.photo_url} size="md" />
            <div className="min-w-0 flex-1">
              <div className="label-mono mb-0.5">
                {user?.role === "admin" ? (lang === "bg" ? "Администратор" : "Administrator") : user?.role === "coach" ? t("coach") : user?.role === "parent" ? t("parent") : user?.role === "academy" ? (lang === "bg" ? "Академия" : "Academy") : (lang === "bg" ? "Играч" : "Player")}
              </div>
              <div className="font-oswald uppercase text-sm truncate">{user?.name}</div>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="p-3 border-b border-white/10 flex justify-center">
            <Avatar name={user?.name} src={user?.photo_url} size="md" />
          </div>
        )}

        <nav className="p-3 space-y-1 flex-1 overflow-y-auto scroll-thin">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${it.to.replace(/^\//, "").replace(/\//g, "-") || "dashboard"}`}
              title={collapsed ? it.label : undefined}
            >
              {({ isActive }) => (
                <span className={`relative flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 group ${
                  isActive ? "bg-pitch/12 text-white shadow-[inset_0_0_0_1px_rgba(59,130,246,0.18)]" : "text-white/60 hover:text-white hover:bg-white/[0.04]"
                } ${collapsed ? "justify-center" : ""}`}>
                  <span className={`absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full ${isActive ? "bg-pitch shadow-[0_0_8px_rgba(59,130,246,0.6)]" : "bg-transparent group-hover:bg-white/20"} transition-all`} />
                  <it.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-pitch" : ""} transition-colors`} />
                  {!collapsed && <span className="font-manrope text-sm">{it.label}</span>}
                  {!collapsed && isActive && <span className="ml-auto w-1.5 h-1.5 bg-pitch rounded-full pulse-ring" />}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-2.5">
          {user?.is_pro && !collapsed && (
            <div className="flex items-center justify-center gap-2 py-1.5 text-xs font-mono tracking-widest text-pitch border border-pitch/30 rounded-md">
              <Sparkles className="w-3 h-3" /> PRO
            </div>
          )}
          {!collapsed && (
            <div className="flex justify-start">
              <LanguageToggle />
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={handleLogout}
              className={`${collapsed ? "w-full" : "flex-1"} flex items-center justify-center gap-2 btn-secondary text-xs`}
              data-testid="logout-button"
              title={collapsed ? t("logout") : undefined}
            >
              <LogOut className="w-4 h-4" /> {!collapsed && t("logout")}
            </button>
            <button
              onClick={toggleCollapsed}
              className="hidden md:flex w-9 h-9 items-center justify-center border border-white/10 hover:border-pitch hover:bg-pitch/5 transition-all rounded-md"
              data-testid="sidebar-collapse"
              title={collapsed ? "Expand" : "Collapse"}
            >
              {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden flex items-center justify-between p-4 header-blur">
        <button onClick={() => setOpen(true)} data-testid="open-sidebar" className="p-1.5 -ml-1.5 rounded-md hover:bg-white/5 transition-colors">
          <Menu className="w-5 h-5" />
        </button>
        <Link to="/" className="flex items-center">
          <Logo size={22} wordmark />
        </Link>
        <div className="flex items-center gap-1">
          <NotificationsBell />
          <button
            onClick={handleLogout}
            className="p-2 rounded-md hover:bg-red-500/10 hover:text-red-300 text-white/55 transition-colors"
            data-testid="mobile-topbar-logout"
            title={t("logout")}
            aria-label={t("logout")}
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 flex flex-col min-w-0">
        {/* Desktop top bar */}
        <div className="hidden md:flex items-center justify-end gap-3 px-8 py-4 border-b border-white/5">
          <NotificationsBell />
        </div>
        <div className="flex-1 p-4 md:p-8 max-w-full overflow-x-hidden pb-24 md:pb-8">{children}</div>
      </main>

      {open && <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-30 md:hidden" onClick={() => setOpen(false)} />}

      {/* Mobile bottom nav */}
      <MobileBottomNav />
    </div>
  );
}
